import { useMemo } from 'react';

import { fg } from '@confluence/feature-gating';

import { resolveQueryShorthands } from '../utils/macroParamUtils.ts';

import type { useContentByLabelDataQuery$variables } from './__generated__/useContentByLabelDataQuery.graphql.ts';

const MAX_RESULTS_CAP = 500;
const DEFAULT_MAX_RESULTS = 15;

/**
 * Block caps applied to the `macroExcerpt.atlasDocFormat` field.
 *
 * "Excerpt preview" (simple) shows a short teaser, while "Page preview"
 * (rich content) shows a substantial portion of the page.
 */
const SIMPLE_EXCERPT_MAX_BLOCKS = 3;
const RICH_CONTENT_EXCERPT_MAX_BLOCKS = 50;

/**
 * The search backend implicitly caps every response at 50 results, whatever
 * `first` asks for. Requesting more does not fail — the extra results are just
 * silently dropped — so a macro configured with a `max` above 50 can only reach
 * the rest by paginating.
 *
 * This is a backend constraint rather than a UX choice, which is why it doubles
 * as the page size: there is no value in requesting more than the backend will
 * ever return. It also bounds how many nested ADF previews mount per request.
 */
export const BACKEND_MAX_RESULTS_PER_REQUEST = 50;

export enum ExcerptType {
	None = 'none',
	Simple = 'simple',
	RichContent = 'rich content',
}

/**
 * Sort options supported by the Content By Label macro.
 *
 * The macro param values (left) are mapped to CQL `order by` field names (right).
 * For example, the macro uses `'creation'` but CQL expects `'created'`.
 */
export enum SortOption {
	Creation = 'created',
	Title = 'title',
	Modified = 'lastModified',
}

const SORT_PARAM_TO_CQL: Record<string, SortOption> = {
	creation: SortOption.Creation,
	title: SortOption.Title,
	modified: SortOption.Modified,
};

function parseSortOption(value: string | undefined): SortOption | undefined {
	if (!value) {
		return undefined;
	}
	return SORT_PARAM_TO_CQL[value];
}

export interface ContentByLabelMacroParams {
	cql?: { value?: string };
	sort?: { value?: string };
	reverse?: { value?: string };
	max?: { value?: string };
	maxResults?: { value?: string };
	title?: { value?: string };
	showLabels?: { value?: string };
	showSpace?: { value?: string };
	excerptType?: { value?: string };
	maxCheckbox?: { value?: string };
}

export interface ParsedContentByLabelParams {
	cql: string;
	sort: SortOption | undefined;
	reverse: boolean;
	max: number;
	maxParseError: boolean;
	title: string;
	showLabels: boolean;
	showSpace: boolean;
	excerptType: ExcerptType;
	limitLabelLinksToSpace: boolean;
	macroParamsForQuery: useContentByLabelDataQuery$variables;
}

function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
	if (value === undefined || value === '') {
		return defaultValue;
	}
	return value === 'true';
}

interface ParseMaxResult {
	max: number;
	maxParseError: boolean;
}

function parseMax(value: string | undefined, maxCheckbox: string | undefined): ParseMaxResult {
	if (maxCheckbox === 'true') {
		return { max: MAX_RESULTS_CAP, maxParseError: false };
	}

	if (value === undefined || value === '') {
		return { max: DEFAULT_MAX_RESULTS, maxParseError: false };
	}

	const parsed = parseInt(value, 10);
	if (isNaN(parsed)) {
		return { max: DEFAULT_MAX_RESULTS, maxParseError: true };
	}

	if (parsed <= 0) {
		return { max: DEFAULT_MAX_RESULTS, maxParseError: false };
	}

	return { max: Math.min(parsed, MAX_RESULTS_CAP), maxParseError: false };
}

/**
 * Maps legacy CQL sort field names to their correct equivalents.
 * Legacy macros may have stored invalid field names directly in the cql param
 * (e.g. 'creation' instead of 'created', 'modified' instead of 'lastModified').
 */
const LEGACY_CQL_SORT_FIELD_REMAP: Record<string, SortOption> = {
	creation: SortOption.Creation,
	modified: SortOption.Modified,
};

/**
 * Normalizes sort field names in an existing ORDER BY clause within a CQL string.
 * Remaps legacy field names (e.g. 'creation', 'modified') to valid CQL field names.
 */
function normalizeCqlSortFields(cql: string): string {
	return cql.replace(/(\border\s+by\s+)(\w+)/gi, (_match, prefix, field) => {
		const lower = field.toLowerCase();
		const remapped = LEGACY_CQL_SORT_FIELD_REMAP[lower];
		return `${prefix}${remapped !== undefined ? remapped : field}`;
	});
}

function buildOrderByClause(sort: SortOption | undefined, reverse: boolean): string {
	if (!sort) {
		return '';
	}

	const direction = reverse ? 'desc' : 'asc';
	return ` order by ${sort} ${direction}`;
}

function parseExcerptType(value: string | undefined): ExcerptType {
	if (value === ExcerptType.Simple) {
		return ExcerptType.Simple;
	}
	if (value === ExcerptType.RichContent) {
		return ExcerptType.RichContent;
	}
	return ExcerptType.None;
}

/**
 * Builds GraphQL query variables from raw macro parameters.
 *
 * This is a pure function (no hooks) so it can be used both at render time
 * and during SSR query preloading.
 *
 * `currentSpaceKey` and `currentContentId` are passed as separate arguments
 * (rather than mixed into `macroParams`) so callers don't need to allocate a
 * merged object on every render — that would defeat memoization in
 * `useParsedMacroParams`.
 */
export function composeMacroParamsForQuery(
	macroParams: ContentByLabelMacroParams | undefined,
	currentSpaceKey: string | undefined,
	currentContentId?: string | undefined,
): useContentByLabelDataQuery$variables {
	const rawCql = macroParams?.cql?.value ?? '';
	const sort = parseSortOption(macroParams?.sort?.value);
	const reverse = parseBoolean(macroParams?.reverse?.value, false);
	const { max } = parseMax(
		macroParams?.max?.value ?? macroParams?.maxResults?.value,
		macroParams?.maxCheckbox?.value,
	);

	const resolvedCql = normalizeCqlSortFields(
		resolveQueryShorthands(rawCql, {
			currentSpaceKey,
			currentContentId,
		}),
	);
	const orderByClause = buildOrderByClause(sort, reverse);
	const cql = resolvedCql + orderByClause;

	const excerptType = parseExcerptType(macroParams?.excerptType?.value);

	return {
		cql,
		after: undefined,
		// Gated here rather than in the data hook so that the SSR preload in
		// `@confluence/preload-macros` and the client render agree on `first` —
		// a mismatch would miss the preloaded cache entry and refetch.
		//
		// The ungated branch still asks for the full `max` even though the backend
		// will only ever return 50. That is deliberate: it leaves the control group
		// byte-for-byte unchanged, so if the 50 cap turns out not to hold for every
		// query, turning the gate off is a genuine rollback rather than a second
		// behaviour change.
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		first: fg('cc_rewrite_contentbylabel_paginate_results')
			? Math.min(max, BACKEND_MAX_RESULTS_PER_REQUEST)
			: max,
		includeExcerpt: excerptType === ExcerptType.Simple || excerptType === ExcerptType.RichContent,
		fallbackToBody: excerptType === ExcerptType.RichContent,
		maxExcerptBlocks:
			excerptType === ExcerptType.RichContent
				? RICH_CONTENT_EXCERPT_MAX_BLOCKS
				: SIMPLE_EXCERPT_MAX_BLOCKS,
	};
}

export function useParsedMacroParams(
	macroParams: ContentByLabelMacroParams | undefined,
	currentSpaceKey: string | undefined,
	currentContentId?: string | undefined,
): ParsedContentByLabelParams {
	return useMemo(() => {
		const sort = parseSortOption(macroParams?.sort?.value);
		const reverse = parseBoolean(macroParams?.reverse?.value, false);
		const { max, maxParseError } = parseMax(
			macroParams?.max?.value ?? macroParams?.maxResults?.value,
			macroParams?.maxCheckbox?.value,
		);
		const title = macroParams?.title?.value ?? '';
		const showLabels = parseBoolean(macroParams?.showLabels?.value, true);
		const showSpace = parseBoolean(macroParams?.showSpace?.value, true);
		const excerptType = parseExcerptType(macroParams?.excerptType?.value);

		const macroParamsForQuery = composeMacroParamsForQuery(
			macroParams,
			currentSpaceKey,
			currentContentId,
		);
		const cql = macroParamsForQuery.cql;

		const limitLabelLinksToSpace = cql.includes('space = ');

		return {
			cql,
			sort,
			reverse,
			max,
			maxParseError,
			title,
			showLabels,
			showSpace,
			excerptType,
			limitLabelLinksToSpace,
			macroParamsForQuery,
		};
	}, [macroParams, currentSpaceKey, currentContentId]);
}
